import { proctorApi } from "@/lib/proctorApi";

export interface CameraSession {
  stream: MediaStream;
  stop(): void;
}

export async function startCamera(): Promise<CameraSession> {
  const stream = await navigator.mediaDevices.getUserMedia({
    video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" },
    audio: false,
  });
  return {
    stream,
    stop() { stream.getTracks().forEach((t) => t.stop()); },
  };
}

/**
 * Fires the callback ONCE when the camera track ends or is muted (unplugged,
 * permission revoked, OS lock). The watcher self-detaches on first fire so it
 * doesn't re-notify every polling tick after the track has ended.
 */
export function watchCameraTrack(stream: MediaStream, onLost: () => void): () => void {
  const track = stream.getVideoTracks()[0];
  let done = false;
  const stop = () => {
    if (done) return;
    done = true;
    track?.removeEventListener("ended", fire);
    track?.removeEventListener("mute", fire);
    window.clearInterval(timer);
  };
  const fire = () => {
    if (done) return;
    stop();
    onLost();
  };
  if (!track) { onLost(); return () => {}; }
  track.addEventListener("ended", fire);
  track.addEventListener("mute", fire);
  // Fallback poll — some browsers don't fire "ended" reliably when the OS closes the device.
  const timer = window.setInterval(() => {
    if (track.readyState === "ended") fire();
  }, 3000);
  return stop;
}

export function scheduleSnapshots(params: {
  attemptId: string;
  stream: MediaStream;
  intervalMs: number;
  onError?: (err: unknown) => void;
}): () => void {
  const { attemptId, stream, intervalMs, onError } = params;
  const video = document.createElement("video");
  video.playsInline = true;
  video.muted = true;
  video.srcObject = stream;
  const ready = video.play();

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const capture = async () => {
    try {
      await ready;
      const w = video.videoWidth || 640;
      const h = video.videoHeight || 480;
      if (w === 0 || h === 0 || !ctx) return;
      canvas.width = w;
      canvas.height = h;
      ctx.drawImage(video, 0, 0, w, h);
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.7));
      if (!blob) return;
      await proctorApi.uploadSnapshot(attemptId, blob, w, h);
    } catch (e) {
      onError?.(e);
    }
  };

  const timer = window.setInterval(capture, Math.max(5_000, intervalMs));
  return () => {
    window.clearInterval(timer);
    video.pause();
    video.srcObject = null;
  };
}
